import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormGroup, Form, Input, Label, Button } from 'reactstrap';
import { useForm } from 'hooks/useForm';
import AlertModal from 'components/AlertModal';
import { useAlert } from 'hooks';
import config from 'config';

interface FormValues {
  email: string;
  code: string;
  password: string;
  confirmPassword: string;
}

// https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/email#validation
// eslint-disable-next-line max-len
const mailPattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export default function PasswordNM() {
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [isMailSent, setIsMailSent] = useState(false);
  const [isCodeVerified, setIsCodeVerified] = useState(false);

  const [showAlert, message, alert] = useAlert(false, '');
  const [values, handleChange] = useForm<FormValues>({
    email: '',
    code: '',
    password: '',
    confirmPassword: '',
  });

  const handleSubmitEmail = async () => {
    try {
      setIsLoading(true);

      if (!values.email) {
        alert(true, 'Tiene que ingresar el email para poder recuperar la contraseña');
        return;
      }

      if (!mailPattern.test(values.email)) {
        alert(true, 'El email ingresado no es válido');
        return;
      }

      const res = await fetch(`${ config.baseUrl }/no-matriculates/reset-password/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: values.email }),
      });

      if (res.status !== 200) {
        alert(true, 'No se pudo enviar el correo de recuperación');
        return;
      }

      const payload = await res.json();
      if (!payload || !payload.status || payload.status !== 'ok') {
        alert(true, 'No se pudo enviar el correo de recuperación');
        return;
      }

      alert(true, 'Le hemos enviado un código de recuperación por correo electrónico.');

      setIsMailSent(true);
    } catch (error) {
      console.error(error);
      alert(true, 'No se pudo enviar el correo de recuperación');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnterCode = async () => {
    try {
      setIsLoading(true);

      if (!values.email) {
        alert(true, 'Tiene que ingresar el email para poder validar su código');
        return;
      }

      if (!mailPattern.test(values.email)) {
        alert(true, 'El email ingresado no es válido');
        return;
      }

      alert(true, 'Ingrese su código de recuperación.');

      setIsMailSent(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitCode = async () => {
    try {
      setIsLoading(true);

      if (!isMailSent) {
        alert(true, 'Tiene que enviar el código de recuperación por correo electrónico primero');
        return;
      }

      if (!values.code) {
        alert(true, 'Tiene que ingresar el codigo para poder recuperar la contraseña');
        return;
      }

      if (!/^\d{5}$/.test(values.code)) {
        alert(true, 'El código ingresado no es válido');
        return;
      }

      const res = await fetch(`${ config.baseUrl }/no-matriculates/reset-password/code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: values.email, code: values.code }),
      });

      if (res.status !== 200) {
        alert(true, 'No se pudo verificar el código');
        return;
      }

      const payload = await res.json();
      if (!payload || !payload.status || payload.status !== 'ok') {
        alert(true, 'No se pudo verificar el código');
        return;
      }

      setIsCodeVerified(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      setIsLoading(true);

      if (!isMailSent) {
        alert(true, 'Tiene que enviar el código de recuperación por correo electrónico primero');
        return;
      }

      if (!isCodeVerified) {
        alert(true, 'Tiene que verificar el código de recuperación por correo electrónico primero');
        return;
      }

      if (!values.password) {
        alert(true, 'Tiene que ingresar la nueva contraseña');
        return;
      }

      if (!values.confirmPassword) {
        alert(true, 'Tiene que confirmar la nueva contraseña');
        return;
      }

      if (values.password !== values.confirmPassword) {
        alert(true, 'Las contraseñas no coinciden');
        return;
      }

      const res = await fetch(`${ config.baseUrl }/no-matriculates/reset-password/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: values.email,
          code: values.code,
          password: values.password,
        }),
      });

      if (res.status !== 200) {
        alert(true, 'No se pudo cambiar la contraseña');
        return;
      }

      const payload = await res.json();
      if (!payload || !payload.status || payload.status !== 'ok') {
        alert(true, 'No se pudo cambiar la contraseña');
        return;
      }

      alert(
        true,
        'La contraseña ha sido cambiada correctamente. Ahora será redirigido a la pantalla de inicio de sesión',
      );

      setTimeout(() => {
        history.push('/login');
      }, 5000);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AlertModal isOpen={ showAlert } message={ message } onClose={ () => alert(false) } />
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center',
        width: '320px',
      }}>
        <img src={ require('../../logo-40.png') } alt="COKIBA" />
        <br /><br />
        <Form>
          {
            !isMailSent && (
              <>
                <FormGroup>
                  <Label htmlFor="email">Correo Electrónico</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Ingresar el correo electrónico"
                    value={ values.email }
                    className="text-center"
                    onChange={ handleChange }
                    disabled={ isLoading }
                  />
                </FormGroup>
                <Button
                  color="primary"
                  onClick={ handleSubmitEmail }
                  disabled={ isLoading }
                >
                  {
                    isLoading
                      ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      : <>Enviar correo de recuperación</>
                  }
                </Button>
                <Button
                  color="warning"
                  onClick={ handleEnterCode }
                  disabled={ isLoading }
                >
                  {
                    isLoading
                      ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      : <>Ya tengo un código</>
                  }
                </Button>
              </>
            )
          }
          {
            isMailSent && !isCodeVerified && (
              <>
                <FormGroup>
                  <Label htmlFor="code">Código de recuperación</Label>
                  <Input
                    id="code"
                    name="code"
                    type="text"
                    placeholder="Ingresar el código de recuperación"
                    className="text-center"
                    onChange={ handleChange }
                    disabled={ isLoading }
                  />
                </FormGroup>
                <Button
                  color="primary"
                  onClick={ handleSubmitCode }
                  disabled={ isLoading }
                >
                  {
                    isLoading
                      ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      : <>Validar código</>
                  }
                </Button>
              </>
            )
          }
          {
            isCodeVerified && (
              <>
                <FormGroup>
                  <Label htmlFor="password">Nueva contraseña</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Ingresar la nueva contraseña"
                    className="text-center"
                    autoComplete="new-password"
                    onChange={ handleChange }
                    disabled={ isLoading }
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirmar la nueva contraseña"
                    className="text-center"
                    autoComplete="new-password"
                    onChange={ handleChange }
                    disabled={ isLoading }
                  />
                </FormGroup>
                <Button
                  color="primary"
                  onClick={ handleChangePassword }
                  disabled={ isLoading }
                >
                  {
                    isLoading
                      ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      : <>Cambiar contraseña</>
                  }
                </Button>
              </>
            )
          }
        </Form>
      </div>
    </>
  );
}
